<script setup lang="ts">
import { computed } from "vue";

import { searchItemSelected } from "@/analytics/events/search";
import { trackEvent } from "@/analytics/track";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { windowFromRelative } from "@/math/coordinate-transform";
import { scrollSmoothTo, visibleCenterCoord } from "@/math/coordinate-utils";
import type { ResolvedCard } from "@/model/card";
import type { SearchResultKey } from "@/model/search";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";

import ListItemStickySearch from "./ListItemStickySearch.vue";

const emit = defineEmits<{ select: [item: SearchResultKey] }>();

const props = defineProps<{
  results: ResolvedCard[];
  selected?: SearchResultKey;
  boardId: string;
}>();

const isOnCurrentBoard = computed(() => useBoardStore().boardId() === props.boardId);

function isSelected(item: ResolvedCard) {
  return item.id === props.selected?.id && item.team?.id === props.selected?.teamId;
}

function select(item: ResolvedCard) {
  const otherBoardId = isOnCurrentBoard.value ? undefined : props.boardId;
  trackItemSelected(item, otherBoardId);
  emit("select", { id: item.id, boardId: otherBoardId, teamId: item.team?.id });
}

function moveIntoView(item: ResolvedCard) {
  const card = useBoardStore().currentBoard().cards[item.id];
  if (card) {
    const selected = isSelected(item);
    scrollSmoothTo(windowFromRelative(card.meta.pos), visibleCenterCoord(), !selected);
    if (!selected) select(item);
  }
}

// used for analytics
function trackItemSelected(_: ResolvedCard, boardId?: string) {
  const currentBoardType = useBoardStore().currentBoard().type;
  const targetBoardType = boardId ? useBoardsStore().boards[boardId].type : undefined;

  trackEvent(searchItemSelected(currentBoardType, targetBoardType));
}
</script>

<template>
  <ListItemStickySearch
    v-for="item in results"
    :id="`result-${item.id}`"
    :key="item.id"
    :data-id="item.id"
    class="search-result-list base-search-item result-card"
    :active="isSelected(item)"
    :item
    :board-id
    @click.stop="select(item)"
  >
    <template v-if="isOnCurrentBoard" #actions>
      <IconButton
        class="move-into-view"
        icon="arrow/move"
        size="small"
        :text="$t('label.search.moveIntoView')"
        @click.stop="moveIntoView(item)"
      />
    </template>
  </ListItemStickySearch>
</template>

<style lang="scss">
// Can't make this scoped because the scoping doesn't seem to work
// with ListItemStickySearch (ListItem's inheritAttr: false might be the culprit)
@use "@/styles/variables/colors";

li:not(:hover, :focus-within) .move-into-view {
  opacity: 0;
}

.search-result-list.base-search-item {
  &:focus-visible {
    background-color: colors.$background-grey-light;
    cursor: pointer;

    .high-contrast-mode & {
      // In high-contrast mode, the background color alone has too low contrast
      outline: 2px solid colors.$outline;
      outline-offset: -2px;
    }
  }
}
</style>
